import type { BridgeInstance } from "./bridge";

/** Result of `bridge.handshake` — lets the SPA gate features on what the host wired up. */
export interface HandshakeResult {
  protocolVersion: number;
  hostPlatform: "ios" | "android" | "web";
  hostVersion: string;
  /** Present only when the web host is running inside the Desktop App (Electron or the legacy .NET wrapper). */
  hostDesktopVersion?: string;
  supportedEvents: (BridgeEventName | (string & {}))[];
}

/**
 * Credential bundle returned by `auth.getCreds`. The host owns and mints this (per-org token).
 * `@repo/auth` defines its own structurally-identical `Creds` so it stays bridge-independent.
 */
export interface GetCredsResult {
  token: string;
  userId: string;
  orgId: string;
  expiresAt?: string;
}

export type CloseReason = "user" | "idle" | "error";

/**
 * Host-pushed context change. Generic so org, conversation, and future surfaces ride one event:
 * `type` discriminates; `id` is the entity id; `metadata` carries type-specific extras.
 */
export interface ContextUpdate {
  type: "conversation" | "org" | (string & {});
  id: string;
  metadata: Record<string, string>;
}

export const EVENTS = {
  handshake: "bridge.handshake",
  getCreds: "auth.getCreds",
  close: "ui.close",
  contextUpdate: "context.update",
} as const;

/** Union of the v1 event wire names ("bridge.handshake" | "auth.getCreds" | ...). */
export type BridgeEventName = (typeof EVENTS)[keyof typeof EVENTS];

export interface TypedEvents {
  handshake(spaVersion: string): Promise<HandshakeResult>;
  getCreds(): Promise<GetCredsResult>;
  close(reason?: CloseReason): Promise<void>;
  onContextUpdate(handler: (ctx: ContextUpdate) => void): () => void;
}

/** Typed wrappers over the four v1 events. Raw `callNativeBridge`/`on` remain for new events. */
export function createEvents(instance: BridgeInstance): TypedEvents {
  return {
    handshake(spaVersion) {
      return instance.callNativeBridge<HandshakeResult>(EVENTS.handshake, { spaVersion });
    },
    getCreds() {
      return instance.callNativeBridge<GetCredsResult>(EVENTS.getCreds);
    },
    close(reason) {
      return reason === undefined
        ? instance.callNativeBridge<void>(EVENTS.close)
        : instance.callNativeBridge<void>(EVENTS.close, { reason });
    },
    onContextUpdate(handler) {
      return instance.bridge.on(EVENTS.contextUpdate, (payload) =>
        handler(payload as ContextUpdate),
      );
    },
  };
}
